import { useCallback } from 'react';

import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';

import {
  ServiceTypeEnum,
  SpaceTypeEnum,
  usePayersRetrieveQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { serviceTypeOptionsMap } from 'src/features/add-trip/ServiceDetails/serviceTypeOptions';
import { transportTypeOptionsMap } from 'src/features/add-trip/ServiceDetails/transportTypeOptions';

import { Alert } from '../../Alert/Alert';
import { NoPriceSummaryFoundContentCriteriaRow } from './NoPriceSummaryFoundContentCriteriaRow';

export interface NoPriceSummaryFoundContentProps {
  payerId: string;
  dateOfTrip: string;
  spaceType: SpaceTypeEnum;
  serviceType: ServiceTypeEnum;
  payerName?: string;
  loadedMiles?: number | null;
}

export function NoPriceSummaryFoundContent({
  payerId,
  dateOfTrip,
  spaceType,
  serviceType,
  payerName,
  loadedMiles,
}: NoPriceSummaryFoundContentProps) {
  const spaceTypeLabel = transportTypeOptionsMap[spaceType];
  const serviceTypeLabel = serviceTypeOptionsMap[serviceType];

  const dateTimeOfTrip = DateTime.fromISO(dateOfTrip);
  const formattedDateOfTrip = dateTimeOfTrip.isValid
    ? dateTimeOfTrip.toLocaleString(DateTime.DATE_MED)
    : 'Unknown'; //shouldn't happen but we should handle it gracefully

  // during a quote request, the payer name is not available in the trip object
  // on summary modal, the payer name is available in the trip object
  const { data: payerData } = usePayersRetrieveQuery(
    { id: payerId },
    { skip: !payerId || !!payerName }
  );

  const payerNameToDisplay = payerName || payerData?.display_name || 'Unknown';

  const onGoToPricing = useCallback(() => {
    const encodedPayerId = encodeURIComponent(payerId);
    const encodedSpaceType = encodeURIComponent(spaceType);
    const encodedDateOfTrip = encodeURIComponent(dateOfTrip);
    const url = `/pricing?payerId=${encodedPayerId}&spaceType=${encodedSpaceType}&tripDate=${encodedDateOfTrip}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [payerId, spaceType, dateOfTrip]);

  return (
    <div className="flex flex-col gap-4">
      <Alert
        title="No Matching Pricing Scheme Found"
        message="We searched for a pricing scheme matching this trip's criteria but found no match. A price cannot be generated until a matching scheme is configured."
        severity="warning"
      />

      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Criteria Checked
        </p>
        <NoPriceSummaryFoundContentCriteriaRow
          label="Payer"
          value={payerNameToDisplay}
        />
        <NoPriceSummaryFoundContentCriteriaRow
          label="Date of Trip"
          value={formattedDateOfTrip}
        />
        <NoPriceSummaryFoundContentCriteriaRow
          label="Space Type"
          value={spaceTypeLabel}
        />
        <NoPriceSummaryFoundContentCriteriaRow
          label="Service Type"
          value={serviceTypeLabel}
        />
        {!!loadedMiles && (
          <NoPriceSummaryFoundContentCriteriaRow
            label="Trip Miles"
            value={loadedMiles.toFixed(2)}
          />
        )}
      </div>

      <div className="rounded-md border border-teal-200 bg-teal-50 p-4">
        <p className="text-sm font-semibold text-teal-800">What to do next</p>
        <p className="mt-1 text-sm text-teal-700">
          Create or update a pricing scheme that covers this trip&apos;s
          criteria to resolve this issue.
        </p>
        <button
          type="button"
          onClick={onGoToPricing}
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-teal-600 px-3 py-1.5 text-sm font-medium text-teal-700 hover:bg-teal-100"
        >
          Go to Pricing Schemes
          <ArrowRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
